import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import Layout from "../layouts/layout";
import Spinner from "../components/spinner";
import BILLING from "../api/services/BILLING";
import PLANS from "../api/services/PLANS";
import CUSTOMERS from "../api/services/CUSTOMERS";
import PRODUCTS from "../api/services/PRODUCTS";
import SERVICES from "../api/services/SERVICES";
import { useBusinessCapabilities } from "../hooks/useBusinessCapabilities";
import styles from "../styles/_screens/cashRegister.module.scss";

// ── helpers ──────────────────────────────────────────────────────────────────
const fmtPrice = (n) => {
  const num = Number(n);
  if (isNaN(num)) return "0";
  return num % 1 === 0 ? String(num) : num.toFixed(2);
};

let clientSearchTimer = null;

// ─────────────────────────────────────────────────────────────────────────────
export default function CashRegister() {
  // ── Client ──
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSearch, setClientSearch] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [clientSearching, setClientSearching] = useState(false);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientRef = useRef(null);

  // ── Catalog ──
  const [activeTab, setActiveTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [plans, setPlans] = useState([]);
  const [services, setServices] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);

  // ── Custom item ──
  const [customName, setCustomName] = useState("");
  const [customPrice, setCustomPrice] = useState("");

  // ── Cart ──
  const [cart, setCart] = useState([]); // { id, type, name, price, quantity, itemId }

  // ── Discount ──
  const [discountType, setDiscountType] = useState("fixed"); // fixed | percent
  const [discountValue, setDiscountValue] = useState("");

  // ── Payment ──
  const [paymentMethod, setPaymentMethod] = useState("credit");
  const [cardNumber, setCardNumber] = useState("");
  const [exp, setExp] = useState("");
  const [cvv, setCvv] = useState("");
  const [holderId, setHolderId] = useState("");

  // ── Invoice ──
  const [docType, setDocType] = useState("receipt");
  const [morningAccount, setMorningAccount] = useState(null);

  // ── Submit ──
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // { documentNumber, viewUrl }

  // ── PRO check (only credit-card charging requires PRO) ──
  const { isPro } = useBusinessCapabilities();

  // ─────────────────────────────────────────
  //  FETCH CATALOG DATA ON MOUNT
  // ─────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      setCatalogLoading(true);
      try {
        const businessId = JSON.parse(localStorage.getItem("user_id") || '""');
        const [prodRes, planRes, svcRes] = await Promise.allSettled([
          PRODUCTS.get_all(),
          PLANS.get_all_plans(),
          SERVICES.get_all({ business_id: businessId }),
        ]);

        // get() returns body directly, post() returns axios response
        if (prodRes.status === "fulfilled") {
          const body = prodRes.value;
          const raw = body?.data || [];
          setProducts(Array.isArray(raw) ? raw.filter(p => p.active !== false) : []);
        }
        if (planRes.status === "fulfilled") {
          const body = planRes.value;
          const raw = body?.data?.plans || body?.data || [];
          setPlans(Array.isArray(raw) ? raw.filter(p => p.isActive !== false && !p.isDeleted) : []);
        }
        if (svcRes.status === "fulfilled") {
          // post() returns axios response → .data is the body
          const body = svcRes.value?.data || svcRes.value;
          const raw = body?.data || [];
          setServices(Array.isArray(raw) ? raw : []);
        }
      } catch (err) {
        console.log("catalog load error", err);
      }
      setCatalogLoading(false);
    };

    load();
  }, []);

  // ── Fetch morning account (for invoice type logic) ──
  useEffect(() => {
    BILLING.get_morning_account()
      .then((res) => setMorningAccount(res))
      .catch(() => setMorningAccount(null));
  }, []);

  // ─────────────────────────────────────────
  //  CLIENT SEARCH
  // ─────────────────────────────────────────
  const searchClients = useCallback((query) => {
    if (!query || query.length < 2) {
      setClientResults([]);
      setShowClientDropdown(false);
      return;
    }
    clearTimeout(clientSearchTimer);
    clientSearchTimer = setTimeout(async () => {
      setClientSearching(true);
      try {
        const res = await CUSTOMERS.get(1, 10, "all", query);
        const items = res?.data || [];
        setClientResults(Array.isArray(items) ? items : []);
        setShowClientDropdown(true);
      } catch (err) {
        console.log("client search error", err);
        setClientResults([]);
      }
      setClientSearching(false);
    }, 400);
  }, []);

  useEffect(() => {
    searchClients(clientSearch);
  }, [clientSearch, searchClients]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (clientRef.current && !clientRef.current.contains(e.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectClient = (c) => {
    const name = c.user?.name || c.userId?.name || c.name || "";
    const phone = c.user?.phone || c.phone || c.userId?.phone || "";
    const realUserId = c.user?._id || c.userId?._id || c.userId || c._id;
    setSelectedClient({ ...c, name, phone, realUserId });
    setClientSearch("");
    setClientResults([]);
    setShowClientDropdown(false);
  };

  const clearClient = () => {
    setSelectedClient(null);
    setClientSearch("");
  };

  // ─────────────────────────────────────────
  //  CART ACTIONS
  // ─────────────────────────────────────────
  const addToCart = (item) => {
    setCart((prev) => {
      const key = item.id || item._id || `${item.type}-${item.name}`;
      const existing = prev.find((c) => c.id === key);
      if (existing) {
        return prev.map((c) =>
          c.id === key ? { ...c, quantity: c.quantity + 1 } : c
        );
      }
      return [
        ...prev,
        {
          id: key,
          type: item.type,
          name: item.name,
          price: Number(item.price) || 0,
          quantity: 1,
          itemId: item._id || item.id || null,
        },
      ];
    });
  };

  const changeQty = (id, delta) => {
    setCart((prev) =>
      prev
        .map((c) =>
          c.id === id ? { ...c, quantity: Math.max(0, c.quantity + delta) } : c
        )
        .filter((c) => c.quantity > 0)
    );
  };

  const removeFromCart = (id) => {
    setCart((prev) => prev.filter((c) => c.id !== id));
  };

  const addCustomItem = () => {
    if (!customName.trim() || !customPrice) return;
    const price = Number(customPrice);
    if (isNaN(price) || price <= 0) {
      toast.error("נא להזין מחיר תקין");
      return;
    }
    const id = `custom-${Date.now()}`;
    setCart((prev) => [
      ...prev,
      { id, type: "custom", name: customName.trim(), price, quantity: 1, itemId: null },
    ]);
    setCustomName("");
    setCustomPrice("");
  };

  // ─────────────────────────────────────────
  //  TOTALS
  // ─────────────────────────────────────────
  const subtotal = cart.reduce((sum, c) => sum + c.price * c.quantity, 0);

  let discountAmount = 0;
  if (discountValue && Number(discountValue) > 0) {
    if (discountType === "percent") {
      discountAmount = (subtotal * Math.min(Number(discountValue), 100)) / 100;
    } else {
      discountAmount = Math.min(Number(discountValue), subtotal);
    }
  }

  const total = Math.max(0, subtotal - discountAmount);

  // ─────────────────────────────────────────
  //  SUBMIT CHECKOUT
  // ─────────────────────────────────────────
  const handleSubmit = async () => {
    if (!selectedClient) {
      toast.error("נא לבחור לקוח");
      return;
    }
    if (cart.length === 0) {
      toast.error("העגלה ריקה");
      return;
    }
    if (paymentMethod === "credit") {
      if (!isPro) {
        toast.error("סליקת אשראי דורשת מנוי PRO פעיל");
        return;
      }
      if (!cardNumber || !exp || !cvv) {
        toast.error("נא למלא פרטי כרטיס אשראי");
        return;
      }
    }

    setSubmitting(true);
    try {
      // UserBusiness doc → extract actual userId
      const actualUserId = selectedClient?.realUserId || selectedClient?.user?._id || selectedClient?.userId || selectedClient?._id || null;
      const clientEmail = selectedClient?.user?.email || selectedClient?.email || "";
      const payload = {
        userId: actualUserId,
        clientName: selectedClient?.name || "לקוח אורח",
        clientPhone: selectedClient?.phone || "",
        clientEmail,
        items: cart.map((c) => ({
          name: c.name,
          type: c.type,
          itemId: c.itemId,
          price: c.price,
          quantity: c.quantity,
        })),
        discount: discountAmount > 0 ? discountAmount : 0,
        paymentMethod,
        docType: paymentMethod !== "debt" ? docType : undefined,
        ...(paymentMethod === "credit" && {
          cardDetails: { cardNumber, exp, cvv, holderId },
        }),
      };

      const res = await BILLING.pos_checkout(payload);
      setSuccess({
        documentNumber: res?.documentNumber || res?.data?.documentNumber || "",
        viewUrl: res?.viewUrl || res?.data?.viewUrl || "",
      });
      toast.success("התשלום בוצע בהצלחה!");
    } catch (err) {
      console.log("checkout error", err);
      toast.error(
        err?.response?.data?.message || "שגיאה בביצוע התשלום"
      );
    }
    setSubmitting(false);
  };

  // ─────────────────────────────────────────
  //  RESET (new sale)
  // ─────────────────────────────────────────
  const resetAll = () => {
    setSelectedClient(null);
    setClientSearch("");
    setCart([]);
    setDiscountType("fixed");
    setDiscountValue("");
    setPaymentMethod("credit");
    setCardNumber("");
    setExp("");
    setCvv("");
    setHolderId("");
    setDocType("receipt");
    setSuccess(null);
  };

  // ─────────────────────────────────────────
  //  DRAG & DROP
  // ─────────────────────────────────────────
  const [dragOverCart, setDragOverCart] = useState(false);

  const handleDragStart = (e, item) => {
    e.dataTransfer.setData("application/json", JSON.stringify(item));
    e.dataTransfer.effectAllowed = "copy";
  };

  const handleCartDrop = (e) => {
    e.preventDefault();
    setDragOverCart(false);
    try {
      const item = JSON.parse(e.dataTransfer.getData("application/json"));
      if (item) addToCart({ ...item, id: item._id || item.id });
    } catch (_) {}
  };

  const handleCartDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    setDragOverCart(true);
  };

  const handleCartDragLeave = () => setDragOverCart(false);

  // ─────────────────────────────────────────
  //  RENDER CATALOG ITEMS
  // ─────────────────────────────────────────
  const renderCatalogItems = () => {
    if (activeTab === "custom") {
      return (
        <div className={styles.customItemForm}>
          <div className={styles.fieldGroup}>
            <label>שם פריט</label>
            <input
              type="text"
              placeholder="לדוגמה: תספורת ילד"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
            />
          </div>
          <div className={styles.fieldGroup}>
            <label>מחיר</label>
            <input
              type="number"
              placeholder="0"
              min="0"
              value={customPrice}
              onChange={(e) => setCustomPrice(e.target.value)}
            />
          </div>
          <button
            onClick={addCustomItem}
            disabled={!customName.trim() || !customPrice}
          >
            הוסף
          </button>
        </div>
      );
    }

    let items = [];
    if (activeTab === "products") {
      items = products.map((p) => ({
        _id: p._id,
        type: "product",
        name: p.name || p.title || "",
        price: p.price || 0,
        desc: p.description || "",
      }));
    } else if (activeTab === "plans") {
      items = plans.map((p) => ({
        _id: p._id,
        type: "plan",
        name: p.name || "",
        price: Number(p.price) || 0,
        desc: [
          p.isSubscription ? "מנוי חודשי" : "כרטיסיה",
          p.numberOfTurns > 0 ? `${p.numberOfTurns} כניסות` : "",
        ].filter(Boolean).join(" · "),
      }));
    } else if (activeTab === "services") {
      items = services.map((s) => ({
        _id: s._id,
        type: "service",
        name: s.name || "",
        price: Number(s.price) || 0,
        desc: [
          s.time ? `${s.time} דק'` : "",
          s.type === "CLASS" ? "שיעור" : "",
          s.employeeId?.name || "",
        ].filter(Boolean).join(" · "),
      }));
    }

    if (catalogLoading) {
      return (
        <div className={styles.loadingWrap}>
          <Spinner />
        </div>
      );
    }

    if (items.length === 0) {
      return <div className={styles.emptyMsg}>אין פריטים להצגה</div>;
    }

    return (
      <div className={styles.catalogList}>
        {items.map((item) => (
          <div
            key={item._id}
            className={styles.catalogItem}
            draggable
            onDragStart={(e) => handleDragStart(e, item)}
          >
            <div className={styles.dragHandle}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><circle cx="9" cy="5" r="1.5"/><circle cx="15" cy="5" r="1.5"/><circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/><circle cx="9" cy="19" r="1.5"/><circle cx="15" cy="19" r="1.5"/></svg>
            </div>
            <div className={styles.catalogItemInfo}>
              <span className={styles.itemName}>{item.name}</span>
              {item.desc && (
                <span className={styles.itemDesc}>{item.desc}</span>
              )}
            </div>
            {item.price > 0 && (
              <span className={styles.catalogItemPrice}>
                {fmtPrice(item.price)} &#8362;
              </span>
            )}
            <button
              className={styles.addBtn}
              onClick={() =>
                addToCart({
                  ...item,
                  id: item._id,
                })
              }
            >
              +
            </button>
          </div>
        ))}
      </div>
    );
  };

  // ─────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────
  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.wrapper}>
          <h1>קופה</h1>

          <div className={styles.columns}>
            {/* ─── LEFT: Client + Catalog ─────────────────────── */}
            <div className={styles.leftCol}>
              {/* Client selector */}
              <div className={styles.clientSection}>
                <div className={styles.sectionLabel}>לקוח</div>

                {selectedClient ? (
                  <div>
                    <div className={styles.clientChip}>
                      {selectedClient.name}
                      {selectedClient.phone && ` - ${selectedClient.phone}`}
                      <button onClick={clearClient}>&times;</button>
                    </div>
                  </div>
                ) : (
                  <div className={styles.clientSearchWrap} ref={clientRef}>
                    <input
                      className={styles.clientSearchInput}
                      type="text"
                      placeholder="חיפוש לקוח לפי שם או טלפון..."
                      value={clientSearch}
                      onChange={(e) => setClientSearch(e.target.value)}
                      onFocus={() => {
                        if (clientResults.length > 0) setShowClientDropdown(true);
                      }}
                    />
                    {clientSearching && (
                      <div className={styles.loadingWrap} style={{ padding: 8 }}>
                        <Spinner />
                      </div>
                    )}
                    {showClientDropdown && clientResults.length > 0 && (
                      <div className={styles.clientDropdown}>
                        {clientResults.map((c) => {
                          const name = c.user?.name || c.userId?.name || c.name || "";
                          const phone = c.user?.phone || c.phone || "";
                          return (
                            <div
                              key={c._id}
                              className={styles.clientOption}
                              onClick={() => selectClient(c)}
                            >
                              {name}
                              {phone && <span>{phone}</span>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div className={styles.guestSection}>
                      <div className={styles.guestLabel}>או הזן פרטי לקוח אורח:</div>
                      <div className={styles.guestFields}>
                        <input
                          type="text"
                          placeholder="שם לקוח *"
                          id="guestName"
                          onChange={() => {}}
                        />
                        <input
                          type="tel"
                          placeholder="טלפון *"
                          id="guestPhone"
                          onChange={() => {}}
                        />
                        <button
                          onClick={() => {
                            const name = document.getElementById("guestName").value.trim();
                            const phone = document.getElementById("guestPhone").value.trim();
                            if (!name) { toast.error("נא להזין שם לקוח"); return; }
                            if (!phone) { toast.error("נא להזין מספר טלפון של הלקוח"); return; }
                            setSelectedClient({ name, phone, _id: null, isGuest: true });
                            setShowClientDropdown(false);
                          }}
                        >
                          אישור
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Catalog tabs */}
              <div className={styles.catalogTabs}>
                {[
                  { key: "products", label: "מוצרים" },
                  { key: "plans", label: "מנויים" },
                  { key: "services", label: "שירותים" },
                  { key: "custom", label: "פריט חופשי" },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    className={`${styles.catalogTab} ${
                      activeTab === tab.key ? styles.catalogTabActive : ""
                    }`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Catalog items */}
              {renderCatalogItems()}
            </div>

            {/* ─── RIGHT: Cart + Payment ─────────────────────── */}
            <div
              className={`${styles.rightCol} ${dragOverCart ? styles.dropActive : ""}`}
              onDrop={handleCartDrop}
              onDragOver={handleCartDragOver}
              onDragLeave={handleCartDragLeave}
            >
              {/* Cart title */}
              <h2 className={styles.cartTitle}>
                עגלה
                {cart.length > 0 && (
                  <span className={styles.cartBadge}>{cart.length}</span>
                )}
              </h2>

              {/* Cart items */}
              <div className={styles.cart}>
                {cart.length === 0 ? (
                  <div className={styles.cartEmpty}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>
                    <p>גררו פריטים לכאן או לחצו +</p>
                  </div>
                ) : (
                  cart.map((item) => (
                    <div key={item.id} className={styles.cartItem}>
                      <div className={styles.cartItemInfo}>
                        <div className={styles.cartItemName}>{item.name}</div>
                        <div className={styles.cartItemUnit}>
                          {fmtPrice(item.price)} &#8362; ליחידה
                        </div>
                      </div>

                      <div className={styles.cartQty}>
                        <button onClick={() => changeQty(item.id, -1)}>-</button>
                        <span>{item.quantity}</span>
                        <button onClick={() => changeQty(item.id, 1)}>+</button>
                      </div>

                      <div className={styles.cartItemTotal}>
                        {fmtPrice(item.price * item.quantity)} &#8362;
                      </div>

                      <button
                        className={styles.cartRemove}
                        onClick={() => removeFromCart(item.id)}
                      >
                        &times;
                      </button>
                    </div>
                  ))
                )}
              </div>

              {/* Discount */}
              <div className={styles.discountSection}>
                <div className={styles.discountToggle}>
                  <button
                    className={discountType === "fixed" ? styles.discountActive : ""}
                    onClick={() => setDiscountType("fixed")}
                  >
                    &#8362; סכום
                  </button>
                  <button
                    className={discountType === "percent" ? styles.discountActive : ""}
                    onClick={() => setDiscountType("percent")}
                  >
                    % אחוז
                  </button>
                </div>
                <input
                  className={styles.discountInput}
                  type="number"
                  min="0"
                  placeholder="הנחה"
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                />
              </div>

              {/* Totals */}
              <div className={styles.totalsSection}>
                <div className={styles.totalRow}>
                  <span>סכום ביניים</span>
                  <span>{fmtPrice(subtotal)} &#8362;</span>
                </div>
                {discountAmount > 0 && (
                  <div className={styles.discountRow}>
                    <span>
                      הנחה
                      {discountType === "percent"
                        ? ` (${discountValue}%)`
                        : ""}
                    </span>
                    <span>-{fmtPrice(discountAmount)} &#8362;</span>
                  </div>
                )}
                <div className={styles.totalRowFinal}>
                  <span>סה"כ</span>
                  <span>{fmtPrice(total)} &#8362;</span>
                </div>
              </div>

              {/* Payment method */}
              <div className={styles.sectionLabel}>אמצעי תשלום</div>
              <div className={styles.paymentMethods}>
                {[
                  { key: "credit", label: "אשראי" },
                  { key: "cash", label: "מזומן" },
                  { key: "bank", label: "העברה" },
                  { key: "check", label: "צ'ק" },
                  { key: "debt", label: "חוב" },
                ].map((m) => (
                  <button
                    key={m.key}
                    className={`${styles.paymentBtn} ${
                      paymentMethod === m.key ? styles.paymentBtnActive : ""
                    }`}
                    onClick={() => setPaymentMethod(m.key)}
                  >
                    {m.label}
                  </button>
                ))}
              </div>

              {/* Credit card form */}
              {paymentMethod === "credit" && (
                <div className={styles.cardForm}>
                  <div className={styles.fieldGroup}>
                    <label>מספר כרטיס</label>
                    <input
                      type="text"
                      placeholder="0000 0000 0000 0000"
                      value={cardNumber}
                      onChange={(e) => setCardNumber(e.target.value)}
                      maxLength={19}
                      dir="ltr"
                      style={{ textAlign: "left" }}
                    />
                  </div>
                  <div className={styles.cardRow}>
                    <div className={styles.fieldGroup}>
                      <label>תוקף (MM/YY)</label>
                      <input
                        type="text"
                        placeholder="MM/YY"
                        value={exp}
                        onChange={(e) => setExp(e.target.value)}
                        maxLength={5}
                        dir="ltr"
                        style={{ textAlign: "left" }}
                      />
                    </div>
                    <div className={styles.fieldGroup}>
                      <label>CVV</label>
                      <input
                        type="text"
                        placeholder="***"
                        value={cvv}
                        onChange={(e) => setCvv(e.target.value)}
                        maxLength={4}
                        dir="ltr"
                        style={{ textAlign: "left" }}
                      />
                    </div>
                  </div>
                  <div className={styles.fieldGroup}>
                    <label>ת.ז. בעל הכרטיס</label>
                    <input
                      type="text"
                      placeholder="מספר ת.ז."
                      value={holderId}
                      onChange={(e) => setHolderId(e.target.value)}
                      maxLength={9}
                    />
                  </div>
                </div>
              )}

              {/* Invoice type (hidden when payment = debt) */}
              {paymentMethod !== "debt" && (
                <>
                  <div className={styles.sectionLabel}>סוג מסמך</div>
                  <div className={styles.invoiceTypes}>
                    {[
                      { key: "receipt", label: "קבלה" },
                      { key: "invoice_receipt", label: "חשבונית מס קבלה" },
                      { key: "invoice", label: "חשבונית" },
                    ].map((d) => (
                      <button
                        key={d.key}
                        className={`${styles.invoiceBtn} ${
                          docType === d.key ? styles.invoiceBtnActive : ""
                        }`}
                        onClick={() => setDocType(d.key)}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {/* Submit */}
              <button
                className={styles.submitBtn}
                disabled={cart.length === 0 || submitting}
                onClick={handleSubmit}
              >
                {submitting
                  ? "מעבד..."
                  : `ביצוע תשלום ${fmtPrice(total)} \u20AA`}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Success overlay ── */}
      {success && (
        <div className={styles.successOverlay}>
          <div className={styles.successCard}>
            <div className={styles.successCheck}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <h2 className={styles.successTitle}>התשלום בוצע בהצלחה!</h2>
            {success.documentNumber && (
              <p className={styles.successDoc}>
                מסמך מספר: {success.documentNumber}
              </p>
            )}
            <div className={styles.successActions}>
              {success.viewUrl && (
                <a
                  className={styles.viewDocBtn}
                  href={success.viewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  צפייה במסמך
                </a>
              )}
              <button className={styles.newSaleBtn} onClick={resetAll}>
                מכירה חדשה
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
